<template>
  <div>
    <i :class="['pi pi-ellipsis-h', $style.icon]" @click="handleMeatballClick" />
    <Popover ref="dropdownRef">
      <div v-for="option in dropdownOptions" :key="option.text" :class="$style.tooltipWrapper">
        <div :class="[$style.dropdownListItem, { [$style.disabledListItem]: option.disable }]" @click="option.callback">
          <VdIcon store="fas" :name="option.icon" size="large" color="#BCBEC1" />
          <span :class="$style.listItemText">{{ option.text }}</span>
        </div>

        <OfferABLink
          v-if="isLinkedToAbTest && option.tooltipContent && abTestId"
          :ab-test-id="abTestId"
          :tooltip-text="option.tooltipContent"
        />
        <!-- Join-linked only (no scalar test to link to): plain info tooltip -->
        <VdIcon
          v-else-if="isLinkedToAbTest && option.tooltipContent"
          v-tooltip.right="option.tooltipContent"
          store="fas"
          name="circle-info"
          size="xlarge"
          custom-size="14px"
          color="var(--vd-color-blue-default)"
        />
      </div>
    </Popover>
  </div>
</template>

<script setup>
import VdIcon from "@/components/VdIcon/VdIcon.vue";
import Popover from "primevue/popover";
import { useConfirm } from "primevue/useconfirm";
import { computed, ref } from "vue";
import OfferABLink from "./OfferABLink.vue";

const emit = defineEmits([
  "duplicate",
  "delete",
  "mark-as-ready",
  "mark-as-draft",
  "stopOffer",
  "cancel-stopping-the-offer",
]);
const props = defineProps({
  status: {
    type: String,
    required: true,
  },
  abTestId: {
    type: String,
    default: null,
  },
  abtestLinks: {
    type: Array,
    default: () => [],
  },
  useMultiLink: {
    type: Boolean,
    default: false,
  },
  isOfferReady: {
    type: Boolean,
    default: false,
  },
});

const isPartOfAbTest = computed(() => !!props.abTestId);
// Multi-link: links live in OfferAbTest rows too (the scalar may be null), so linkage is the union
const isLinkedToAbTest = computed(() =>
  props.useMultiLink ? props.abtestLinks.length > 0 || isPartOfAbTest.value : isPartOfAbTest.value
);

const confirm = useConfirm();

const textMap = {
  delete: "This offer is a part of an AB test. Delete the AB test to delete this offer",
  markAsReady: "This offer is a part of an AB test. Mark the Ab test as ready to mark this offer ready.",
  markAsDraft: "This offer is a part of an AB test. Mark the Ab test as draft to mark this offer as draft.",
  stopOffer: "This offer is a part of an AB test. Stop the Ab test to stop this offer.",
  cancelStopOffer:
    "This offer is a part of an AB test. cancel the termination of the Ab test to cancel it for this offer.",
  deleteMultiLink: "This offer is linked to an A/B test. Remove its links before deleting it.",
  markAsDraftMultiLink: "This offer is linked to an A/B test. Remove its links before moving it back to draft.",
};

const menu = computed(() => {
  if (props.useMultiLink) {
    // Multi-link: offer status is decoupled from A/B-test linking, so linked offers may be stopped,
    // republished or marked ready — only "back to draft" (and delete) stay blocked while linked.
    return {
      DRAFT: [
        { text: "Duplicate", disable: false },
        {
          text: "Delete",
          disable: isLinkedToAbTest.value,
          tooltipContent: textMap.deleteMultiLink,
        },
        { text: "Mark as ready", disable: !props.isOfferReady },
      ],
      PENDING: [
        { text: "Duplicate", disable: false },
        {
          text: "Mark as draft",
          disable: isLinkedToAbTest.value,
          tooltipContent: textMap.markAsDraftMultiLink,
        },
      ],
      PUBLISHED: [
        { text: "Duplicate", disable: false },
        { text: "Stop the Offer", disable: false },
      ],
      TERMINATING: [
        { text: "Duplicate", disable: false },
        { text: "Cancel stopping the offer", disable: false },
      ],
      STOPPED: [{ text: "Duplicate", disable: false }],
    };
  }
  return {
    DRAFT: [
      { text: "Duplicate", disable: false },
      {
        text: "Delete",
        disable: isPartOfAbTest.value,
        tooltipContent: textMap.delete,
      },
      {
        text: "Mark as ready",
        disable: isPartOfAbTest.value || !props.isOfferReady,
        tooltipContent: textMap.markAsReady,
      },
    ],
    PENDING: [
      { text: "Duplicate", disable: false },
      {
        text: "Mark as draft",
        disable: isPartOfAbTest.value,
        tooltipContent: textMap.markAsDraft,
      },
    ],
    PUBLISHED: [
      { text: "Duplicate", disable: false },
      {
        text: "Stop the Offer",
        disable: isPartOfAbTest.value,
        tooltipContent: textMap.stopOffer,
      },
    ],
    TERMINATING: [
      { text: "Duplicate", disable: false },
      {
        text: "Cancel stopping the offer",
        disable: isPartOfAbTest.value,
        tooltipContent: textMap.cancelStopOffer,
      },
    ],
    STOPPED: [{ text: "Duplicate", disable: false }],
  };
});

const dropdownRef = ref();

const handleMeatballClick = (event) => {
  event.stopPropagation();
  dropdownRef.value.toggle(event);
};

const handleMenuOptionEvent = (event, eventToEmit = null) => {
  if (eventToEmit) {
    emit(eventToEmit);
  }
  dropdownRef.value.toggle(event);
};

const optionsIcons = {
  Delete: {
    icon: "fa-trash",
    callback: (event) => {
      confirm.require({
        message: "Are you sure want to delete this offer?",
        header: "Confirmation",
        rejectClass: "p-button-secondary p-button-outlined",
        acceptClass: "p-button-danger",
        acceptIcon: "pi pi-trash",
        rejectLabel: "Cancel",
        acceptLabel: "Delete",
        accept: () => {
          handleMenuOptionEvent(event, "delete");
        },
        reject: () => {
          handleMenuOptionEvent(event);
        },
      });
    },
  },
  Duplicate: {
    icon: "fa-clone",
    callback: (event) => {
      handleMenuOptionEvent(event, "duplicate");
    },
  },
  "Mark as ready": {
    icon: "fa-check-circle",
    callback: (event) => {
      handleMenuOptionEvent(event, "mark-as-ready");
    },
  },
  "Mark as draft": {
    icon: "fa-eye",
    callback: (event) => {
      handleMenuOptionEvent(event, "mark-as-draft");
    },
  },
  "Stop the Offer": {
    icon: "fa-times",
    callback: (event) => {
      confirm.require({
        message: "Are you sure want to stop this offer?",
        header: "Confirmation",
        rejectClass: "p-button-secondary p-button-outlined",
        acceptClass: "p-button-danger",
        acceptIcon: "pi pi-times",
        rejectLabel: "Cancel",
        acceptLabel: "Stop offer",
        accept: () => {
          handleMenuOptionEvent(event, "stopOffer");
        },
        reject: () => {
          handleMenuOptionEvent(event);
        },
      });
    },
  },
  "Cancel stopping the offer": {
    icon: "fa-times",
    callback: (event) => {
      handleMenuOptionEvent(event, "cancel-stopping-the-offer");
    },
  },
};

const dropdownOptions = computed(() => {
  const options = menu.value[props.status];
  return options
    .map((option) => ({
      text: option.text,
      icon: optionsIcons[option.text].icon,
      callback: optionsIcons[option.text].callback,
      disable: option.disable,
      tooltipContent: option.tooltipContent,
    }))
    .sort((a, b) => a.text.localeCompare(b.text));
});
</script>

<style module>
.dropdownListItem {
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: flex-start;
  width: 240px;
  padding: 8px 8px;

  &:hover {
    background-color: var(--vd-color-neutral-light-10);
    cursor: pointer;
  }
}

.listItemText {
  font-size: 14px;
  color: var(--vd-color-text-light-2);
}

.disabledListItem {
  opacity: 0.5;
  pointer-events: none;
}

.icon {
  color: var(--vd-color-blue-default);
  font-weight: 900;
  font-size: 16px;
  cursor: pointer;
}

.tooltipWrapper {
  display: flex;
  gap: 8px;
  align-items: center;
}
</style>
